<template>
  <b-modal
    id="invite_teammate_modal"
    modal-class="modal-invite-teammate"
    centered
    size="md"
    hide-footer
    hide-header-close
    @shown="onShown"
    @hidden="onHidden"
  >
    <button
      class="close-icon"
      @click="closeModal"
    >
      <feather-icon
        icon="XIcon"
        size="20"
      />
    </button>
    <div class="modal-invite-body">
      <div class="modal-invite-header mb-1">
        Invite Teammate
      </div>
      <div class="sub-title mb-2">
        Enter teammate details to send an invitation
      </div>

      <validation-observer
        ref="inviteForm"
        #default="{ invalid }"
      >
        <b-form @submit.prevent="onSubmit">
          <validation-provider
            #default="{ errors }"
            name="Email"
            rules="required|email"
          >
            <b-form-group
              label="Email"
              label-class="pb-0"
            >
              <b-form-input
                v-model="form.email"
                placeholder="Enter email address"
                :state="errors.length > 0 ? false : null"
                :disabled="isSubmitting"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>

          <validation-provider
            #default="{ errors }"
            name="First Name"
            rules="required"
          >
            <b-form-group
              label="First Name"
              label-class="pb-0"
            >
              <b-form-input
                v-model="form.firstName"
                placeholder="Enter first name"
                :state="errors.length > 0 ? false : null"
                :disabled="isSubmitting"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>

          <validation-provider
            #default="{ errors }"
            name="Last Name"
            rules="required"
          >
            <b-form-group
              label="Last Name"
              label-class="pb-0"
            >
              <b-form-input
                v-model="form.lastName"
                placeholder="Enter last name"
                :state="errors.length > 0 ? false : null"
                :disabled="isSubmitting"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>
          </validation-provider>

          <div class="d-flex justify-content-end mt-2">
            <b-button
              variant="outline-secondary"
              class="mr-1"
              :disabled="isSubmitting"
              @click="closeModal"
            >
              Cancel
            </b-button>
            <b-button
              variant="primary"
              type="submit"
              :disabled="invalid || isSubmitting"
            >
              <b-spinner
                v-if="isSubmitting"
                small
                class="mr-50"
              />
              {{ isSubmitting ? 'Sending...' : 'Send Invite' }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal,
  BForm,
  BFormGroup,
  BFormInput,
  BButton,
  BSpinner,
} from 'bootstrap-vue'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import { apiToastSuccess, apiToastWarning } from '@/@core/utils/toast'
import { INVITE_RETAILER_TEAMMATE } from '@/store/modules/connections.module'
import analytics from '@/@core/utils/analytics'
import constants from '@/constants'

const { RETAILER_TEAMMATES } = constants.TRACKS.ACTIONS

export default {
  name: 'InviteTeammateModal',
  components: {
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BButton,
    BSpinner,
    ValidationObserver,
    ValidationProvider,
    FeatherIcon,
  },
  props: {
    retailerId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      isSubmitting: false,
      form: {
        email: '',
        firstName: '',
        lastName: '',
      },
    }
  },
  methods: {
    async onSubmit() {
      if (!(await this.validateForm())) return

      this.trackSubmitInvite()
      this.isSubmitting = true
      try {
        await this.inviteTeammate()
        this.handleInviteSuccess()
      } catch (err) {
        this.handleInviteError(err)
      } finally {
        this.isSubmitting = false
      }
    },
    async validateForm() {
      const isValid = await this.$refs.inviteForm.validate()
      return isValid && this.retailerId
    },
    trackSubmitInvite() {
      analytics.track(RETAILER_TEAMMATES.BRAND_SUBMITS_INVITE_TEAMMATE, {
        retailerId: this.retailerId,
        email: this.form.email,
      })
    },
    async inviteTeammate() {
      return this.$store.dispatch(INVITE_RETAILER_TEAMMATE, {
        retailerId: this.retailerId,
        payload: {
          email: this.form.email,
          firstName: this.form.firstName,
          lastName: this.form.lastName,
        },
      })
    },
    handleInviteSuccess() {
      apiToastSuccess('Teammate invitation sent successfully')
      analytics.track(RETAILER_TEAMMATES.BRAND_INVITE_TEAMMATE_SUCCESS, {
        retailerId: this.retailerId,
        email: this.form.email,
      })
      this.$emit('invited')
      this.closeModal()
    },
    getErrorMessage(err) {
      if (!err?.response?.data) return 'Failed to send invitation'
      return err.response.data.details?.message || err.response.data.message || 'Failed to send invitation'
    },
    handleInviteError(err) {
      const errorMessage = this.getErrorMessage(err)
      apiToastWarning(errorMessage)
      analytics.track(RETAILER_TEAMMATES.BRAND_INVITE_TEAMMATE_FAILED, {
        retailerId: this.retailerId,
        email: this.form.email,
        error: errorMessage,
      })
    },
    onShown() {
      analytics.track(RETAILER_TEAMMATES.BRAND_VIEWS_INVITE_TEAMMATE_MODAL, {
        retailerId: this.retailerId,
      })
    },
    closeModal() {
      this.$bvModal.hide('invite_teammate_modal')
    },
    onHidden() {
      analytics.track(RETAILER_TEAMMATES.BRAND_CLOSES_INVITE_TEAMMATE_MODAL, {
        retailerId: this.retailerId,
      })
      this.resetForm()
      this.$emit('close')
    },
    resetForm() {
      this.form = {
        email: '',
        firstName: '',
        lastName: '',
      }
      this.$refs.inviteForm?.reset()
    },
  },
}
</script>

<style lang="scss">
.modal-invite-teammate {
  .modal-invite-body {
    padding: 1rem;
  }

  .modal-invite-header {
    font-weight: 600;
    font-size: 1.25rem;
  }

  .sub-title {
    color: #6c757d;
    font-size: 0.9rem;
  }

  .close-icon {
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
  }
}
</style>
